// The job sheet's views' props, as zod (pages/ux-full components/jobs.js: PhotoStrip). Drawn from
// ready-to-show words the office builds from attachments.list: each file's tile, its kind in a word,
// who took it and when. Not in TOOL_VIEWS: no one tool's answer is these props yet.
import { z } from 'zod';

const Id = z.string().min(1);

/** What a file's place in the strip shows: its thumbnail, a video to play, a file to open, or words while it waits. */
export const PhotoTile = z.discriminatedUnion('t', [
  z.object({ t: z.literal('picture'), src: z.string() }),
  z.object({ t: z.literal('video') }),
  z.object({ t: z.literal('file'), words: z.string(), href: z.string().nullable() }),
  z.object({ t: z.literal('waiting'), words: z.string() }),
]);
export type PhotoTile = z.infer<typeof PhotoTile>;

/** A job's photos, videos and documents (attachments.list), in the order the office chose. */
export const PhotoStripProps = z.object({
  jobNumber: z.string(),
  photos: z.array(
    z.object({
      id: Id,
      /** Its kind in a word: "Before", "The problem", "Other". */
      tag: z.string(),
      /** The note a person wrote on it; null when it has none (a file's own name is no note: P-0161). */
      caption: z.string().nullable(),
      /** Its name in the list: its note, else a made-up one numbered in its category ("Before 2"). */
      name: z.string().optional(),
      /** Who took it and when, in words: "Morgan T., Oct 14, 10:06 a.m."; null when neither is known. */
      by: z.string().nullable(),
      /** On the customer's portal (visibility 'customer'), or the office's only. */
      shared: z.boolean(),
      /** Only a photo's visibility is changed here; a video's or a document's isn't. */
      canShare: z.boolean(),
      tile: PhotoTile,
    }),
  ),
  /** Kept with the job, out of the strip (a message's wrapper, a contact card): a link each. */
  files: z.array(
    z.object({
      id: Id,
      /** Its note, else a made-up name ("Document 1"). */
      name: z.string(),
      type: z.string(),
      href: z.string().nullable(),
      /** The original file's own name, said on the link's title only ("IMG_4532.jpg"). */
      file: z.string().nullable().optional(),
    }),
  ),
  /** How many show before "Show all": a job with hundreds draws a few at first. */
  first: z.int().min(1),
});
export type PhotoStripProps = z.infer<typeof PhotoStripProps>;
